import { getUserData } from '@src/auth/utils'

const PERMISSION_ALIASES = {
  content_library: 'content'
}

export const NAV_PERMISSION_MAP = {
  dashboard: 'dashboard',
  screens: 'screens',
  'content-library': 'content',
  playlist: 'playlists',
  'group-screens': 'groups',
  reports: 'reports',
  health: 'health_monitor'
}

export const PERMISSION_DEFAULT_ROUTES = {
  dashboard: '/dashboard',
  screens: '/screens',
  content: '/content-library',
  playlists: '/playlist',
  groups: '/groups',
  reports: '/reports',
  health_monitor: '/health-monitor'
}

const PERMISSION_ROUTE_ORDER = [
  'dashboard',
  'screens',
  'content',
  'playlists',
  'groups',
  'reports',
  'health_monitor'
]

const ROUTE_PERMISSION_RULES = [
  { pattern: /^\/dashboard(?:\/|$)/, permission: 'dashboard' },
  { pattern: /^\/screens(?:\/|$)/, permission: 'screens' },
  { pattern: /^\/content-library(?:\/|$)/, permission: 'content' },
  { pattern: /^\/contents(?:\/|$)/, permission: 'content' },
  { pattern: /^\/playlist(?:\/|$)/, permission: 'playlists' },
  { pattern: /^\/groups(?:\/|$)/, permission: 'groups' },
  { pattern: /^\/reports(?:\/|$)/, permission: 'reports' },
  { pattern: /^\/health-monitor(?:\/|$)/, permission: 'health_monitor' }
]

const ALWAYS_ALLOWED_ROUTE_PATTERNS = [
  /^\/profile(?:\/|$)/,
  /^\/configurations(?:\/|$)/,
  /^\/misc\/not-authorized(?:\/|$)/
]

export const normalizePermissionKey = (permission) => {
  if (!permission) return ''
  return PERMISSION_ALIASES[permission] || permission
}

const applyPageAccess = (map, rawKey, sourceValue) => {
  const formKey = normalizePermissionKey(rawKey)
  if (!formKey) return

  if (Array.isArray(sourceValue)) {
    map[formKey] = {
      read: sourceValue.includes('read'),
      write: sourceValue.includes('write')
    }
    return
  }

  if (typeof sourceValue === 'boolean') {
    map[formKey] = { read: sourceValue, write: sourceValue }
    return
  }

  if (typeof sourceValue === 'object' && sourceValue !== null) {
    map[formKey] = {
      read: Boolean(sourceValue.read ?? sourceValue.can_read ?? sourceValue.access),
      write: Boolean(sourceValue.write ?? sourceValue.can_write ?? sourceValue.access)
    }
  }
}

export const normalizePermissions = (permissions) => {
  const map = {}

  if (!permissions) return map

  if (typeof permissions === 'object' && !Array.isArray(permissions)) {
    Object.keys(permissions).forEach((key) => {
      applyPageAccess(map, key, permissions[key])
    })
    return map
  }

  if (Array.isArray(permissions)) {
    permissions.forEach((item) => {
      if (typeof item === 'string') {
        applyPageAccess(map, item, ['read', 'write'])
        return
      }

      const rawKey = item.page_key || item.page || item.module || item.key
      const access = []
      if (item.read ?? item.can_read) access.push('read')
      if (item.write ?? item.can_write) access.push('write')
      if (!access.length && (item.access ?? item.enabled)) {
        access.push('read', 'write')
      }

      applyPageAccess(map, rawKey, access.length ? access : Boolean(item.access))
    })
  }

  return map
}

export const normalizePermissionCodes = (codes) => {
  if (!Array.isArray(codes)) return []
  return [...new Set(codes.map((code) => normalizePermissionKey(code)).filter(Boolean))]
}

export const extractSubUserAuthPermissions = (response = {}) => {
  const permissions = normalizePermissions(response.permissions)
  const permissionCodes = normalizePermissionCodes(response.permission_codes)

  if (permissionCodes.length) {
    return { permissions, permission_codes: permissionCodes }
  }

  const derivedCodes = Object.keys(permissions).filter((key) => permissions[key]?.read || permissions[key]?.write)
  return { permissions, permission_codes: derivedCodes }
}

export const PERMISSION_MODULES = {
  DASHBOARD: 'dashboard',
  SCREENS: 'screens',
  CONTENT: 'content',
  PLAYLISTS: 'playlists',
  GROUPS: 'groups',
  REPORTS: 'reports',
  HEALTH_MONITOR: 'health_monitor'
}

export const PERMISSION_DENIED_MESSAGE = 'You do not have permission to perform this action.'

export const isSubUser = () => {
  const user = getUserData()
  return user?.role === 'sub_user'
}

export const getSubUserPermissionMap = () => {
  if (!isSubUser()) return {}
  return normalizePermissions(getUserData()?.permissions)
}

export const getSubUserPermissionCodes = () => {
  if (!isSubUser()) return []

  const user = getUserData()
  const codes = normalizePermissionCodes(user?.permission_codes)
  if (codes.length) return codes

  const permissionMap = getSubUserPermissionMap()
  return Object.keys(permissionMap).filter((key) => permissionMap[key]?.read || permissionMap[key]?.write)
}

export const hasSubUserPageAccess = (pageKey) => {
  if (!isSubUser()) return true

  const key = normalizePermissionKey(pageKey)
  if (!key) return false

  const permissionCodes = getSubUserPermissionCodes()
  if (!permissionCodes.includes(key)) return false

  const access = getSubUserPermissionMap()[key]
  return Boolean(access?.read || access?.write)
}

export const hasSubUserReadAccess = (pageKey) => {
  if (!isSubUser()) return true

  const key = normalizePermissionKey(pageKey)
  if (!key || !getSubUserPermissionCodes().includes(key)) return false

  return Boolean(getSubUserPermissionMap()[key]?.read)
}

export const hasSubUserWriteAccess = (pageKey) => {
  if (!isSubUser()) return true

  const key = normalizePermissionKey(pageKey)
  if (!key || !getSubUserPermissionCodes().includes(key)) return false

  return Boolean(getSubUserPermissionMap()[key]?.write)
}

export const getSubUserPermissions = () => (
  getSubUserPermissionCodes().filter((pageKey) => hasSubUserPageAccess(pageKey))
)

export const hasPermission = (module, action = 'read') => {
  if (!isSubUser()) return true

  const key = normalizePermissionKey(module)
  if (action === 'write') return hasSubUserWriteAccess(key)
  return hasSubUserPageAccess(key)
}

export const isSubUserWriteRouteAllowed = (pathname = '') => {
  if (!isSubUser()) return true

  const path = pathname.split('?')[0]
  const writeRoutes = [
    { pattern: /^\/screens\/create(?:\/|$)/, module: PERMISSION_MODULES.SCREENS },
    { pattern: /^\/screens\/edit\/[^/]+(?:\/|$)/, module: PERMISSION_MODULES.SCREENS },
    { pattern: /^\/playlist\/create(?:\/|$)/, module: PERMISSION_MODULES.PLAYLISTS }
  ]

  const matchedRoute = writeRoutes.find((route) => route.pattern.test(path))
  if (!matchedRoute) return true

  return hasPermission(matchedRoute.module, 'write')
}

export const getSubUserWriteRouteFallback = (pathname = '') => {
  const path = pathname.split('?')[0]

  if (/^\/screens(?:\/|$)/.test(path)) return PERMISSION_DEFAULT_ROUTES.screens
  if (/^\/playlist(?:\/|$)/.test(path)) return PERMISSION_DEFAULT_ROUTES.playlists

  const accessiblePages = getSubUserPermissions()
  const matchedKey = PERMISSION_ROUTE_ORDER.find((key) => accessiblePages.includes(key))
  return matchedKey ? PERMISSION_DEFAULT_ROUTES[matchedKey] : '/dashboard'
}

export const buildAbilitiesForUser = (user) => {
  if (!user) {
    return [{ action: 'manage', subject: 'login' }]
  }

  if (user.isAdvertiser === true) {
    return [{ action: 'manage', subject: 'all' }]
  }

  if (user.role === 'sub_user') {
    const permissionMap = normalizePermissions(user.permissions)
    const permissionCodes = normalizePermissionCodes(user.permission_codes)
    const pageKeys = (permissionCodes.length ? permissionCodes : Object.keys(permissionMap))
      .filter((key) => permissionMap[key]?.read || permissionMap[key]?.write)

    return [
      ...pageKeys.map((permission) => ({ action: 'manage', subject: permission })),
      ...pageKeys.filter((permission) => permissionMap[permission]?.read).map((permission) => ({ action: 'read', subject: permission })),
      ...pageKeys.filter((permission) => permissionMap[permission]?.write).map((permission) => ({ action: 'write', subject: permission })),
      { action: 'manage', subject: 'profile' },
      { action: 'manage', subject: 'settings' }
    ]
  }

  return [
    { action: 'manage', subject: 'publisher' },
    { action: 'manage', subject: 'profile' },
    { action: 'manage', subject: 'settings' },
    { action: 'manage', subject: 'login' }
  ]
}

export const getSubUserDefaultRoute = () => {
  const accessiblePages = getSubUserPermissions()
  const matchedKey = PERMISSION_ROUTE_ORDER.find((key) => accessiblePages.includes(key))
  return matchedKey ? PERMISSION_DEFAULT_ROUTES[matchedKey] : '/misc/not-authorized'
}

export const getDefaultRouteForLoggedInUser = () => {
  if (isSubUser()) return getSubUserDefaultRoute()
  return '/screens'
}

export const filterNavigationForUser = (items = []) => {
  if (!isSubUser()) return items

  return items.filter((item) => {
    const requiredPermission = NAV_PERMISSION_MAP[item.id]
    if (requiredPermission === undefined) return false
    return hasSubUserPageAccess(requiredPermission)
  })
}

export const getRoutePermission = (pathname = '') => {
  const path = pathname.split('?')[0]

  if (ALWAYS_ALLOWED_ROUTE_PATTERNS.some((pattern) => pattern.test(path))) {
    return null
  }

  const matchedRule = ROUTE_PERMISSION_RULES.find((rule) => rule.pattern.test(path))
  return matchedRule ? matchedRule.permission : undefined
}

export const isSubUserRouteAllowed = (pathname = '') => {
  if (!isSubUser()) return true

  const requiredPermission = getRoutePermission(pathname)

  if (requiredPermission === null) return true
  if (requiredPermission === undefined) return false

  return hasSubUserPageAccess(requiredPermission)
}
